import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { apiService } from '@/lib/api';
import LenderShell, { LENDER_BLUE, LENDER_NAVY } from '@/components/lender/LenderShell';
import { getLenderActor, money, fmtDate, DRAW_STATUS } from '@/lib/lenderDemo';

export default function LenderDraws() {
  const actor = getLenderActor();
  const navigate = useNavigate();
  const isAdmin = actor?.role === 'bank_admin';
  const [tab, setTab] = useState(isAdmin ? 'all' : 'queue');
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!actor?.id) return;
    apiService.lenderDraws(actor.id, tab).then(({ data }) => setData(data)).catch(() => setData({ draws: [] }));
  }, [actor?.id, tab]);
  useEffect(() => { load(); }, [load]);

  const draws = data?.draws || [];
  const queueLabel = actor?.role === 'construction_dept' ? 'Awaiting final approval' : 'Needs my review';

  return (
    <LenderShell active="draws">
      <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>Draws</h1>
      <p className="text-slate-500 text-sm mb-5">
        {actor?.role === 'construction_dept'
          ? 'Draws the loan officers have approved and passed to you for final approval.'
          : actor?.role === 'loan_officer'
          ? 'Draws your borrowers submitted. Review, send back, or approve — approval is always allowed; missing items are flagged and logged.'
          : 'Every draw across the bank.'}
      </p>

      {!isAdmin && (
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 mb-4">
          {[['queue', queueLabel], ['all', 'All draws']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`draws-tab-${k}`}
              className={`text-sm px-3 py-1.5 rounded-md font-medium ${tab === k ? 'text-white' : 'text-slate-600'}`}
              style={tab === k ? { background: LENDER_BLUE } : undefined}>
              {label}{k === 'queue' && data?.queue_count ? ` (${data.queue_count})` : ''}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3">Borrower / Project</th>
              <th className="px-4 py-3">Draw</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Submitted</th>
              <th className="px-4 py-3">Docs</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {draws.map((d) => {
              const chip = DRAW_STATUS[d.status] || DRAW_STATUS.submitted;
              return (
                <tr key={d.id} data-testid={`draw-row-${d.id}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 cursor-pointer"
                  onClick={() => navigate(`/lender/draws/${d.id}`)}>
                  <td className="px-4 py-3">
                    <div className="font-semibold" style={{ color: LENDER_NAVY }}>{d.borrower_name}</div>
                    <div className="text-xs text-slate-400">{d.project_name}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">#{d.draw_number}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{money(d.amount)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(d.submitted_at)}</td>
                  <td className="px-4 py-3">
                    {d.missing_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                        <AlertTriangle className="w-3.5 h-3.5" />{d.missing_count} missing
                      </span>
                    ) : (
                      <span className="text-[11px] text-emerald-600 font-medium">Complete</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
                </tr>
              );
            })}
            {draws.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nothing here right now.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </LenderShell>
  );
}
